import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import MainNavigation from '../../components/ui/MainNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import ServiceHero from './components/ServiceHero';
import ServiceTabs from './components/ServiceTabs';
import RelatedServices from './components/RelatedServices';
import CustomerTestimonials from './components/CustomerTestimonials';
import TrustSignals from './components/TrustSignals';
import ComparisonTool from './components/ComparisonTool';
import Button from '../../components/ui/Button';


const ServiceDetailsPage = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState(null);
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  // Mock user data
  useEffect(() => {
    const mockUser = {
      id: 1,
      name: "John Mukasa",
      email: "john.mukasa@company.ug",
      role: "customer"
    };
    setCurrentUser(mockUser);
  }, []);

  // Mock service data
  useEffect(() => {
    const serviceId = searchParams?.get('id') || '1';
    
    const mockServices = {
      '1': {
        id: '1',
        title: 'Enterprise Network Infrastructure Setup',
        category: 'Infrastructure',
        overview: `Comprehensive network infrastructure design and implementation for enterprise environments. Our solution includes network architecture planning, hardware procurement, installation, configuration, and ongoing support to ensure optimal performance and security.`,
        heroImage: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&h=600&fit=crop',
        gallery: [
          'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&h=300&fit=crop',
          'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=400&h=300&fit=crop',
          'https://images.unsplash.com/photo-1551808525-51a94da548ce?w=400&h=300&fit=crop'
        ],
        keyBenefits: [
          'Scalable network architecture',
          'Enhanced security protocols',
          '24/7 monitoring and support',
          'Reduced downtime',
          'Cost-effective solution',
          'Future-ready infrastructure'
        ],
        stats: {
          implementations: '150+',
          avgDelivery: '4-6 weeks',
          satisfaction: '98%'
        },
        specifications: [
          {
            category: 'Network Components',
            items: [
              'Enterprise-grade switches and routers',
              'Fiber optic and copper cabling',
              'Wireless access points',
              'Network security appliances'
            ]
          },
          {
            category: 'Performance',
            items: [
              'Up to 10Gbps backbone speed',
              '99.9% uptime guarantee',
              'Sub-5ms latency',
              'Scalable to 1000+ users'
            ]
          },
          {
            category: 'Security Features',
            items: [
              'Advanced firewall protection',
              'Intrusion detection system',
              'VPN capabilities',
              'Network access control'
            ]
          },
          {
            category: 'Management',
            items: [
              'Centralized network management',
              'Real-time monitoring',
              'Automated backup systems',
              'Performance analytics'
            ]
          }
        ],
        requirements: {
          minimum: [
            '50+ users',
            'Basic IT infrastructure',
            'Dedicated server room',
            'Power backup system'
          ],
          recommended: [
            '100+ users',
            'Existing network foundation',
            'Climate-controlled environment',
            'UPS and generator backup'
          ],
          enterprise: [
            '500+ users',
            'Multi-location setup',
            'Redundant power systems',
            'Dedicated IT team'
          ]
        },
        implementationSteps: [
          {
            title: 'Site Assessment & Planning',
            description: 'Comprehensive evaluation of current infrastructure and requirements analysis',
            duration: '1-2 weeks'
          },
          {
            title: 'Design & Architecture',
            description: 'Network topology design and equipment specification',
            duration: '1 week'
          },
          {
            title: 'Procurement & Staging',
            description: 'Equipment procurement and pre-configuration',
            duration: '2-3 weeks'
          },
          {
            title: 'Installation & Configuration',
            description: 'On-site installation and system configuration',
            duration: '1-2 weeks'
          },
          {
            title: 'Testing & Optimization',
            description: 'System testing, performance optimization, and documentation',
            duration: '1 week'
          },
          {
            title: 'Training & Handover',
            description: 'Staff training and project handover',
            duration: '3-5 days'
          }
        ],
        timeline: {
          planning: '2-3 weeks',
          implementation: '3-4 weeks',
          deployment: '1 week'
        },
        caseStudies: [
          {
            client: 'Uganda Revenue Authority',
            industry: 'Government',
            challenge: 'Outdated network infrastructure causing frequent downtime and security vulnerabilities across multiple locations.',
            solution: 'Implemented a robust, secure network infrastructure with redundancy and centralized management across 15 locations.',
            results: [
              { value: '99.8%', metric: 'Uptime' },
              { value: '60%', metric: 'Faster Processing' },
              { value: '80%', metric: 'Reduced Issues' },
              { value: '15', metric: 'Locations Connected' }
            ]
          },
          {
            client: 'Makerere University',
            industry: 'Education',
            challenge: 'Inadequate network capacity to support growing student population and digital learning initiatives.',
            solution: 'Deployed high-capacity network infrastructure with wireless coverage across campus and student accommodation.',
            results: [
              { value: '10,000+', metric: 'Users Supported' },
              { value: '95%', metric: 'Campus Coverage' },
              { value: '5x', metric: 'Speed Increase' },
              { value: '24/7', metric: 'Support Available' }
            ]
          }
        ],
        pricingTiers: [
          {
            name: 'Basic',
            price: 'UGX 25M',
            period: 'project',
            features: [
              'Up to 50 users',
              'Basic network setup',
              'Standard security',
              '6 months support',
              'Basic monitoring'
            ]
          },
          {
            name: 'Professional',
            price: 'UGX 45M',
            period: 'project',
            popular: true,
            features: [
              'Up to 200 users',
              'Advanced network design',
              'Enhanced security',
              '12 months support',
              'Advanced monitoring',
              'Redundancy options'
            ]
          },
          {
            name: 'Enterprise',
            price: 'Custom Quote',
            period: 'project',
            features: [
              'Unlimited users',
              'Custom architecture',
              'Enterprise security',
              '24/7 support',
              'Full redundancy',
              'Dedicated account manager'
            ]
          }
        ],
        comparisonData: {
          'Implementation Time': '4-6 weeks',
          'Starting Price': 'UGX 25M',
          'Support Level': '24/7 Premium',
          'Scalability': 'Up to 1000+ users',
          'Integration Options': 'Full API Support',
          'Training Included': 'Yes',
          'Warranty Period': '2 years',
          'Customization Level': 'High'
        },
        rating: 4.8,
        startingPrice: 'UGX 25M',
        deliveryTime: '4-6 weeks'
      }
    };

    const selectedService = mockServices?.[serviceId];
    if (selectedService) {
      setService(selectedService);
    }
    setLoading(false);
  }, [searchParams]);

  // Mock related services
  const relatedServices = [
    {
      id: '2',
      title: 'Cloud Migration Services',
      category: 'Cloud Solutions',
      description: 'Seamless migration of your business applications and data to secure cloud platforms with minimal downtime.',
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400&h=300&fit=crop',
      rating: 4.7,
      startingPrice: 'UGX 15M',
      deliveryTime: '3-4 weeks'
    },
    {
      id: '3',
      title: 'Cybersecurity Assessment',
      category: 'Security',
      description: 'Comprehensive security audit and implementation of advanced cybersecurity measures to protect your digital assets.',
      image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=400&h=300&fit=crop',
      rating: 4.9,
      startingPrice: 'UGX 8M',
      deliveryTime: '2-3 weeks'
    },
    {
      id: '4',
      title: 'IT Consultancy Services',
      category: 'Consultancy',
      description: 'Strategic IT planning and consultancy to align your technology infrastructure with business objectives.',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop',
      rating: 4.6,
      startingPrice: 'UGX 5M',
      deliveryTime: '1-2 weeks'
    }
  ];

  // Mock testimonials
  const testimonials = [
    {
      id: 1,
      name: 'Sarah Nakamya',
      position: 'IT Director',
      company: 'Bank of Uganda',
      companySize: 'enterprise',
      rating: 5,
      review: `TechTribes delivered an exceptional network infrastructure solution that exceeded our expectations. The implementation was smooth, and their ongoing support has been outstanding.`,
      date: '2 months ago'
    },
    {
      id: 2,
      name: 'David Okello',
      position: 'Operations Manager',
      company: 'Kampala SME Ltd',
      companySize: 'sme',
      rating: 5,
      review: `The team's expertise and professionalism were evident throughout the project. Our network performance has improved dramatically, and downtime is now virtually non-existent.`,
      date: '1 month ago'
    },
    {
      id: 3,
      name: 'Grace Atim',position: 'ICT Coordinator',company: 'Ministry of Health',companySize: 'government',
      rating: 4,
      review: `Professional service delivery with attention to detail. The network infrastructure has significantly improved our operational efficiency across all departments.`,
      date: '3 weeks ago'
    },
    {
      id: 4,
      name: 'Michael Ssebunya',position: 'CEO',company: 'Innovation Hub Uganda',companySize: 'sme',rating: 5,review: `Excellent project management and technical expertise. The new infrastructure has enabled us to scale our operations and serve more clients effectively.`,date: '2 weeks ago'
    },
    {
      id: 5,
      name: 'Jennifer Namuli',position: 'IT Manager',company: 'Makerere University',companySize: 'enterprise',
      rating: 5,
      review: `The implementation was completed on schedule and within budget. The performance improvements have been remarkable, and student satisfaction has increased significantly.`,
      date: '1 week ago'
    },
    {
      id: 6,
      name: 'Robert Kizza',position: 'Technical Lead',company: 'Uganda Communications Commission',companySize: 'government',rating: 4,review: `TechTribes provided comprehensive solutions that addressed all our networking challenges. Their post-implementation support has been exceptional.`,date: '4 days ago'
    }
  ];

  // Mock trust signals
  const certifications = [
    { name: 'ISO 27001', description: 'Information Security', icon: 'Shield' },
    { name: 'CISCO Partner', description: 'Certified Partner', icon: 'Award' },
    { name: 'Microsoft Gold', description: 'Gold Partnership', icon: 'Star' },
    { name: 'CompTIA Certified', description: 'Technical Excellence', icon: 'CheckCircle' }
  ];

  const clientLogos = [
    { name: 'Bank of Uganda', logo: 'https://via.placeholder.com/120x60/4F46E5/FFFFFF?text=BoU' },
    { name: 'Makerere University', logo: 'https://via.placeholder.com/120x60/059669/FFFFFF?text=MAK' },
    { name: 'MTN Uganda', logo: 'https://via.placeholder.com/120x60/F59E0B/FFFFFF?text=MTN' },
    { name: 'Airtel Uganda', logo: 'https://via.placeholder.com/120x60/DC2626/FFFFFF?text=Airtel' },
    { name: 'NSSF Uganda', logo: 'https://via.placeholder.com/120x60/7C3AED/FFFFFF?text=NSSF' },
    { name: 'URA', logo: 'https://via.placeholder.com/120x60/0891B2/FFFFFF?text=URA' }
  ];

  const metrics = [
    { icon: 'Users', value: '500+', label: 'Happy Clients', description: 'Across Uganda' },
    { icon: 'Award', value: '150+', label: 'Projects Completed', description: 'Successfully delivered' },
    { icon: 'Clock', value: '99.8%', label: 'Uptime Achieved', description: 'Average across all projects' },
    { icon: 'ThumbsUp', value: '98%', label: 'Client Satisfaction', description: 'Based on feedback' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <MainNavigation user={currentUser} />
        <div className="pt-16 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading service details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-background">
        <MainNavigation user={currentUser} />
        <div className="pt-16 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground mb-4">Service Not Found</h1>
            <p className="text-muted-foreground mb-6">The requested service could not be found.</p>
            <Button variant="default" onClick={() => window.history?.back()}>
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation user={currentUser} />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <BreadcrumbNavigation 
            customBreadcrumbs={[
              { label: 'Home', path: '/', icon: 'Home' },
              { label: 'Product Catalog', path: '/product-catalog', icon: 'Package' },
              { label: service?.title, path: location?.pathname, icon: 'Info', isLast: true }
            ]}
          />
        </div>

        <ServiceHero service={service} />
        <ServiceTabs service={service} />
        <CustomerTestimonials testimonials={testimonials} />
        <TrustSignals 
          certifications={certifications}
          clientLogos={clientLogos}
          metrics={metrics}
        />
        <ComparisonTool 
          currentService={service}
          similarServices={relatedServices}
        />
        <RelatedServices relatedServices={relatedServices} />
      </main>
      {/* Footer */}
      <footer className="bg-muted/50 border-t border-border py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-muted-foreground">
            © {new Date()?.getFullYear()} TechTribes Creations Limited. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ServiceDetailsPage;